import { useState, useEffect } from 'react';
import { fieldValidator } from '../../utils/yupSchemaCreator';
import { truncateLabel } from '../../utils/labels';
import { ComboBox, FormControl, Tooltip } from '@airbus/components-react';
import { useLanguageContextAPI } from '../../context/LanguageContext';

export const getSelectValueFromChangeObj = (obj) => (obj === '' || obj == null ? null : obj.value);

export default function FieldSelect(props) {
  const {
    rowId,
    initialValues,
    name,
    label,
    required,
    disabled,
    blurHandler,
    field,
    language,
    options,
    showDisabledSelectedWarning,
  } = props;

  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false);
  const { t } = useLanguageContextAPI();

  useEffect(() => {
    if (initialValues) {
      const valueObj = options?.find((item) => item.value == initialValues[name]);
      setSelected(valueObj ?? null);
      let isTouched = false;
      if (
        initialValues?.isCommitRow ||
        (initialValues?.status === 'E' && initialValues?.backup?.[name] !== initialValues[name]) ||
        ((initialValues?.status === 'N' || initialValues?.status === 'C') &&
          initialValues?.touched[name])
      ) {
        isTouched = true;
      }
      setTouched(isTouched);
      if (isTouched) {
        const error = fieldValidator(field, initialValues[name], language);
        setError(error);
      }
    }
  }, [initialValues]);

  const handleChange = (value) => {
    const valueObj = options.find((item) => item.value == value);
    const error = fieldValidator(field, value, language);
    setSelected(valueObj);
    setError(error);
  };

  const handleBlur = () => {
    const valueObj = selected;
    const nextValue = valueObj?.value ?? null;
    const initialValue = initialValues?.[name] ?? null;

    if (initialValue === nextValue) {
      return;
    }

    // Do not allow selecting disabled options; make it null.
    if (valueObj?.disabled) {
      setSelected(null);
      blurHandler(name, null);
      return;
    }

    if (field?.onValueChange) {
      const valid = field.onValueChange({
        rowId,
        value: nextValue,
        row: initialValues,
      });
      if (valid === false) {
        if (!initialValues[name]) {
          setSelected(null);
          return;
        }
        const valueObj = options?.find((item) => item.value === initialValues[name]);
        setSelected(valueObj);
        return;
      }
    }

    setSelected(valueObj ?? null);
    blurHandler(name, nextValue);
  };

  const comboBoxElement = (
    <ComboBox
      {...{ name, disabled }}
      size='small'
      options={options ?? []}
      value={selected}
      invalid={showDisabledSelectedWarning || (touched && Boolean(error))}
      getOptionLabel={(option) => truncateLabel(option?.label)}
      getOptionValue={(option) => option.value}
      onChange={(obj) => {
        const value = getSelectValueFromChangeObj(obj);
        handleChange(value);
      }}
      keepInputOnSelect={true}
      allowVirtualization={true}
      onBlur={handleBlur}
      hideClear
    ></ComboBox>
  );

  return (
    <FormControl
      {...{
        disabled,
        required,
        label,
      }}
      id={name}
      error={showDisabledSelectedWarning || (touched && Boolean(error))}
      errorText={touched && error?.msg}
      className='disabled-input'
    >
      {showDisabledSelectedWarning ? (
        <Tooltip
          label={t('invalid_dropdown_value')}
          placement='top'
          style={{ width: 'max-content' }}
        >
          <div>{comboBoxElement}</div>
        </Tooltip>
      ) : (
        comboBoxElement
      )}
    </FormControl>
  );
}
